import type { Editor, Text } from 'slate';

import { createAlineCanvas } from '../../canvas';
import { ptToPx, pxToPt } from '../../util/units';
import type { AlineFontName, LeafFontStyle } from '../internal';
import { isAlineFontName, Queries } from '../internal';

/**
 * Word draws small-caps lowercase letters as uppercase glyphs 2pt smaller
 * (ECMA-376 17.3.2.33). Used when the run's font has no GSUB `smcp` feature,
 * or when the rasterizer cannot apply OpenType small-caps (node-canvas,
 * jsPDF). When both the font and canvas support it, draw/measure use
 * `fontVariantCaps: 'small-caps'` so the shaper picks real small-cap glyphs.
 */
export const SMALL_CAPS_SIZE_REDUCTION_PT = 2;

const FONTS_WITH_SMALL_CAPS_GLYPHS = new Set<AlineFontName>([
  'Aptos Serif',
  'Arial',
  'Calibri',
  'Cambria',
  'Free Serif',
  'Georgia',
  'Times New Roman',
]);

const newlines = [
  0x000a, // line feed
  0x000d, // carriage return
];
const breakingSpaces = [
  0x0009, // character tabulation
  0x0020, // space
  0x2000, // en quad
  0x2001, // em quad
  0x2002, // en space
  0x2003, // em space
  0x2004, // three-per-em space
  0x2005, // four-per-em space
  0x2006, // six-per-em space
  0x2008, // punctuation space
  0x2009, // thin space
  0x200a, // hair space
  0x205f, // medium mathematical space
  0x3000, // ideographic space
];
let lazyCanvas: ReturnType<typeof createAlineCanvas> | null = null;
const cache = new Map<string, Map<string, number>>();

/**
 * Lazy initialization of canvas to avoid font registration during module import.
 */
function getOrCreateContext(): ReturnType<ReturnType<typeof createAlineCanvas>['getContext']> {
  if (!lazyCanvas) lazyCanvas = createAlineCanvas();
  return lazyCanvas.getContext('2d');
}

/**
 * Use CSS font declaration - https://www.w3.org/TR/CSS21/fonts.html#propdef-font.
 *
 * Note: Do NOT use lineHeight in this font string (e.g., 16px/1) as that seems to break NodeJS
 * measurements and breaks server-side PDF serialization.
 *
 * We explicitly specify Arial as the first fallback font (before sans-serif) to improve consistency
 * when rendering characters that are not supported by the preferred font.
 */
export function alineFontString({ fontSizePx, fontStyle, fontType, fontWeight }: LeafFontStyle) {
  return `${fontStyle} ${fontWeight} ${fontSizePx}px ${fontType}, Arial, sans-serif`;
}

export interface TextDrawRun {
  font: LeafFontStyle;
  text: string;
  textNode: Text;
}

export function fontHasSmallCapsGlyphs(font: LeafFontStyle): boolean {
  if (!isAlineFontName(font.fontType)) return false;
  return (
    FONTS_WITH_SMALL_CAPS_GLYPHS.has(font.fontType) ||
    (font.fontType === 'Garamond' && font.fontWeight === 'bold' && font.fontStyle === 'italic') ||
    (font.fontType === 'Palatino' && font.fontWeight === 'bold' && font.fontStyle === 'normal')
  );
}

export function shouldUseSmallCapsGlyphs(
  editor: Pick<Editor, 'rendersOpenTypeFeatures'>,
  font: LeafFontStyle
): boolean {
  if (editor.rendersOpenTypeFeatures === false) return false;
  return fontHasSmallCapsGlyphs(font) && 'fontVariantCaps' in getOrCreateContext();
}

export function getTextDrawRuns(
  text: string,
  font: LeafFontStyle,
  smallCapsActive: boolean,
  textNode: Text,
  options: { splitWordsAndSpaces?: boolean; useSmallCapsGlyphs?: boolean } = {}
): TextDrawRun[] {
  if (options.splitWordsAndSpaces) {
    const runs: TextDrawRun[] = [];
    for (const wordOrSpace of Queries.getWordsAndSpaces(text)) {
      runs.push(
        ...getTextDrawRuns(wordOrSpace, font, smallCapsActive, textNode, {
          useSmallCapsGlyphs: options.useSmallCapsGlyphs,
        })
      );
    }
    return runs;
  }

  if (!smallCapsActive || text.length === 0) return [{ font, text, textNode }];
  if (options.useSmallCapsGlyphs && fontHasSmallCapsGlyphs(font))
    return [
      {
        font: { ...font, fontVariantCaps: 'small-caps' },
        text,
        textNode: drawnRunTextNode(textNode, font),
      },
    ];

  const smallCapsFont: LeafFontStyle = {
    ...font,
    fontSizePx: Math.max(font.fontSizePx - ptToPx(SMALL_CAPS_SIZE_REDUCTION_PT), 1),
  };
  const smallCapsTextNode = drawnRunTextNode(textNode, smallCapsFont);

  const runs: TextDrawRun[] = [];
  for (const char of text) {
    // A letter with a longer capital form (ß is SS, ﬁ is FI) is drawn as that form, smaller.
    const upper = char.toUpperCase();
    const isLowercase = char !== upper;
    const displayText = isLowercase ? upper : char;
    const runFont = isLowercase ? smallCapsFont : font;
    const last = runs.at(-1);
    if (last && last.font.fontSizePx === runFont.fontSizePx) last.text += displayText;
    else
      runs.push({
        font: runFont,
        text: displayText,
        textNode: isLowercase ? smallCapsTextNode : textNode,
      });
  }
  return runs;
}

type MeasureTextOptions = {
  textNode: Text;
  font?: LeafFontStyle;
  /** Do not emit a warning if measureText is used for justified text containing spaces. */
  suppressJustifiedWarning?: true;
  text?: string;
};

export function measureText(
  editor: Pick<Editor, 'measureText' | 'rendersOpenTypeFeatures'>,
  options: MeasureTextOptions
): number {
  const { font, suppressJustifiedWarning, text, textNode } = options;
  const string = text ?? textNode.text;
  const baseFont = font ?? Queries.getLeafFontStyle(textNode);
  const smallCapsActive = Queries.isSmallCapsActive(textNode);
  const useSmallCapsGlyphs = shouldUseSmallCapsGlyphs(editor, baseFont);

  let totalWidth = 0;
  for (const run of getTextDrawRuns(string, baseFont, smallCapsActive, textNode, {
    useSmallCapsGlyphs,
  })) {
    if (editor.measureText) {
      totalWidth += editor.measureText(editor, {
        font: run.font,
        suppressJustifiedWarning,
        text: run.text,
        textNode: run.textNode,
      });
    } else {
      const fontString = alineFontString(run.font);
      const cacheKey = run.font.fontVariantCaps
        ? `${fontString}::${run.font.fontVariantCaps}`
        : fontString;
      let widths = cache.get(cacheKey);
      if (!widths) {
        widths = new Map();
        cache.set(cacheKey, widths);
      }
      const cachedWidth = widths.get(run.text);
      if (cachedWidth !== undefined) totalWidth += cachedWidth;
      else {
        if (!suppressJustifiedWarning && run.text.includes(' '))
          console.warn(
            'measureText was called with a string containing one or more spaces.\n\nThis may be a mistake, since measureText will report an incorrect width for justified text containing spaces. To correctly account for justified text, use Queries.getTextWidthPx instead.\n\nIf justified text is handled elsewhere, or if this instance of measureText will never be called with justified text, use the suppressJustifiedWarning option to hide this warning.\n',
            new Error()?.stack
          );

        const ctx = getOrCreateContext();
        ctx.font = fontString;
        if ('fontVariantCaps' in ctx) ctx.fontVariantCaps = run.font.fontVariantCaps ?? 'normal';
        const width = ctx.measureText(run.text).width;
        widths.set(run.text, width);
        totalWidth += width;
      }
    }
  }
  return totalWidth;
}

function isNewline(char: string): boolean {
  return newlines.indexOf(char.charCodeAt(0)) >= 0;
}

export function isBreakingSpace(char: string): boolean {
  return breakingSpaces.indexOf(char.charCodeAt(0)) >= 0;
}

export function tokenize(text: string): string[] {
  const tokens: string[] = [];
  let token = '';

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (isBreakingSpace(char) || isNewline(char)) {
      if (token !== '') {
        tokens.push(token);
        token = '';
      }

      tokens.push(char);
      continue;
    }

    token += char;
  }

  if (token !== '') tokens.push(token);

  return tokens;
}

function drawnRunTextNode(textNode: Text, font: LeafFontStyle): Text {
  return {
    ...textNode,
    fontSize: { origin: 'INHERITED', size: pxToPt(font.fontSizePx) },
    smallCaps: { active: false, origin: 'INHERITED' },
  };
}
